import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Overline } from "@/components/Reveal";
import { SITE, CAPITAL_RANGES, COUNTRIES } from "@/lib/site";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useCountdown(target) {
  const calc = () => {
    const diff = new Date(target).getTime() - Date.now();
    const clamp = Math.max(diff, 0);
    return {
      d: Math.floor(clamp / 86400000),
      h: Math.floor((clamp % 86400000) / 3600000),
      m: Math.floor((clamp % 3600000) / 60000),
      s: Math.floor((clamp % 60000) / 1000),
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
  }, [target]);
  return t;
}

const pad = (n) => String(n).padStart(2, "0");

const inputCls =
  "w-full bg-transparent border-0 border-b border-ivory/25 rounded-none px-0 py-3 text-ivory font-sans text-base placeholder:text-ivory-muted/40 focus:outline-none focus:border-crimson transition-colors";
const labelCls = "block font-sans text-xs uppercase tracking-[0.15em] text-ivory-muted mb-2";

export function FinalCTA() {
  const t = useCountdown(SITE.deadline);
  const [form, setForm] = useState({ full_name: "", country: "", phone: "", email: "", capital_range: "", has_experience: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.country || !form.phone || !form.email || !form.capital_range || form.has_experience === "") {
      toast.error("Completa todos los campos para continuar.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        full_name: form.full_name.trim(),
        country: form.country,
        phone: form.phone.trim(),
        email: form.email.trim(),
        capital_range: form.capital_range,
        has_experience: form.has_experience === "si",
      });
      setDone(true);
      toast.success("Registro recibido. Te contactaremos muy pronto.");
    } catch (err) {
      toast.error("No se pudo enviar. Revisa tus datos e inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="registro" data-testid="final-cta-section" className="relative py-24 sm:py-32 bg-crimson-dark border-t border-crimson overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-crimson-dark via-crimson-dark to-[#5c0515]" />
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-crimson/40 blur-3xl animate-pulse-glow" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline className="text-ivory">Convocatoria abierta</Overline>
              <h2 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight text-ivory mt-6 leading-[1.02]">
                Territorios limitados. La ronda actual cierra pronto.
              </h2>
              <p className="font-sans text-lg font-light text-ivory/85 mt-6 max-w-md">
                Asignamos un número acotado de licencias por país. Precalifica ahora y agenda tu
                llamada estratégica antes de que se cierre tu zona.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-10">
                <div className="font-sans text-xs uppercase tracking-[0.2em] text-ivory/70 mb-4">Cierre de la ronda</div>
                <div className="flex gap-3" data-testid="countdown">
                  {[["Días", t.d], ["Hrs", t.h], ["Min", t.m], ["Seg", t.s]].map(([lbl, val]) => (
                    <div key={lbl} className="bg-midnight/40 backdrop-blur-sm border border-ivory/15 px-4 py-3 min-w-[64px] text-center">
                      <div className="font-serif text-3xl font-black text-ivory tabular-nums">{pad(val)}</div>
                      <div className="font-sans text-[10px] uppercase tracking-wider text-ivory/60 mt-1">{lbl}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 inline-flex items-center gap-2 border border-ivory/20 px-4 py-2">
                  <span className="w-2 h-2 rounded-full bg-ivory animate-pulse" />
                  <span className="font-sans text-sm text-ivory">Solo {SITE.territoriesLeft} territorios disponibles en esta ronda</span>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.1}>
              <div className="bg-midnight border border-ivory/10 p-8 lg:p-10">
                {done ? (
                  <div className="py-16 text-center" data-testid="form-success">
                    <CheckCircle2 className="w-14 h-14 text-crimson mx-auto" strokeWidth={1.5} />
                    <h3 className="font-serif text-3xl font-bold text-ivory mt-6">Solicitud recibida</h3>
                    <p className="font-sans text-base font-light text-ivory-muted mt-4 max-w-sm mx-auto">
                      Gracias, {form.full_name.split(" ")[0]}. Un asesor revisará tu perfil y te contactará
                      para agendar la llamada estratégica.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="lead-form" className="space-y-7">
                    <div>
                      <div className="font-serif text-2xl font-bold text-ivory">Precalifica para la franquicia</div>
                      <p className="font-sans text-sm font-light text-ivory-muted mt-1">Toma menos de un minuto.</p>
                    </div>

                    <div>
                      <label className={labelCls} htmlFor="full_name">Nombre completo</label>
                      <input id="full_name" data-testid="input-full-name" className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Tu nombre y apellido" />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-7">
                      <div>
                        <label className={labelCls}>País</label>
                        <Select value={form.country} onValueChange={(v) => set("country", v)}>
                          <SelectTrigger data-testid="select-country" className="w-full bg-transparent border-0 border-b border-ivory/25 rounded-none px-0 py-3 text-ivory h-auto focus:ring-0 focus:border-crimson data-[placeholder]:text-ivory-muted/40">
                            <SelectValue placeholder="Selecciona" />
                          </SelectTrigger>
                          <SelectContent className="bg-midnight-light border-ivory/15 text-ivory max-h-64">
                            {COUNTRIES.map((c) => (
                              <SelectItem key={c} value={c} data-testid={`country-${c}`} className="text-ivory focus:bg-crimson focus:text-ivory">{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className={labelCls} htmlFor="phone">Teléfono (WhatsApp)</label>
                        <input id="phone" data-testid="input-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+52 55 0000 0000" />
                      </div>
                    </div>

                    <div>
                      <label className={labelCls} htmlFor="email">Correo electrónico</label>
                      <input id="email" type="email" data-testid="input-email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="tu@correo.com" />
                    </div>

                    <div>
                      <label className={labelCls}>Capital disponible para invertir</label>
                      <Select value={form.capital_range} onValueChange={(v) => set("capital_range", v)}>
                        <SelectTrigger data-testid="select-capital" className="w-full bg-transparent border-0 border-b border-ivory/25 rounded-none px-0 py-3 text-ivory h-auto focus:ring-0 focus:border-crimson data-[placeholder]:text-ivory-muted/40">
                          <SelectValue placeholder="Selecciona un rango" />
                        </SelectTrigger>
                        <SelectContent className="bg-midnight-light border-ivory/15 text-ivory">
                          {CAPITAL_RANGES.map((c) => (
                            <SelectItem key={c} value={c} data-testid={`capital-${c}`} className="text-ivory focus:bg-crimson focus:text-ivory">{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className={labelCls}>¿Tienes experiencia como emprendedor o inversionista?</label>
                      <div className="flex gap-3 mt-1">
                        {[["si", "Sí"], ["no", "No"]].map(([val, lbl]) => (
                          <button
                            key={val}
                            type="button"
                            data-testid={`experience-${val}`}
                            onClick={() => set("has_experience", val)}
                            className={`flex-1 py-3 border font-sans text-sm uppercase tracking-wider transition-all ${
                              form.has_experience === val ? "bg-crimson border-crimson text-ivory" : "bg-transparent border-ivory/25 text-ivory-muted hover:border-ivory"
                            }`}
                          >
                            {lbl}
                          </button>
                        ))}
                      </div>
                    </div>

                    <button
                      type="submit"
                      data-testid="submit-lead-button"
                      disabled={submitting}
                      className="w-full bg-crimson text-ivory font-bold py-4 hover:bg-crimson-dark transition-all uppercase tracking-wider text-sm disabled:opacity-60"
                    >
                      {submitting ? "Enviando..." : "Solicitar mi precalificación"}
                    </button>
                    <p className="font-sans text-xs text-ivory-muted/60 text-center">Tus datos son confidenciales y solo se usan para evaluar tu perfil.</p>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
